// Public property listing page — opened by anyone via the magic-link
// share URL. No auth required. The token is the URL parameter.
//
// DSGVO: NEVER show seller-client identity. The backend already strips
// it from the response, so the frontend just renders what it gets.
//
// UX: hero photo carousel → KPI strip → description → agent contact
// card + inquiry form → footer (impressum / DSGVO).

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, Sparkles, Bot, Phone } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { apiBaseUrl } from '@/lib/api';
import { formatMoney } from '@/lib/formatLocale';

export const Route = createFileRoute('/listing/$token')({
  component: ListingPage,
});

// Vienna panorama shown when a listing has no uploaded photos yet.
// Hosted on the marketing site so we don't have to ship a 1+ MB asset.
const VIENNA_FALLBACK_PHOTO =
  'https://4m-immo.at/wp-content/uploads/2020/07/vienna-2.jpg?id=4318';

// Legacy Serbian enum values (preserved from Supabase) → German labels.
// The CRM is German-first, so the public page must never leak raw `nova_gradnja`.
const BUILDING_TYPE_DE: Record<string, string> = {
  nova_gradnja: 'Neubau',
  stara_gradnja: 'Altbau',
  neubau: 'Neubau',
  altbau: 'Altbau',
  unknown: '—',
};

function germanBuildingType(raw: string | null | undefined): string {
  if (!raw) return '—';
  const key = String(raw).trim().toLowerCase();
  const mapped = BUILDING_TYPE_DE[key];
  return mapped ?? raw;
}

interface ListingPhoto {
  id: string;
  image_url: string;
  is_primary: number;
  sort_order: number;
}

interface ListingAgent {
  full_name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  avatar_key: string | null;
  role: string;
}

interface ListingResponse {
  listing: {
    city: string;
    district: string | null;
    address: string | null;
    street: string | null;
    street_number: string | null;
    postal_code: string | null;
    floor: string | null;
    building_type: string | null;
    object_type: string | null;
    rooms_count: string | null;
    property_size: number | null;
    property_area: number | null;
    purchase_price: number | null;
    primary_image_url: string | null;
    marketing_stage: string;
    created_at: number;
  };
  photos: ListingPhoto[];
  agent: ListingAgent | null;
}

function ListingPage() {
  const { t, i18n } = useTranslation();
  const { token } = Route.useParams();
  const [data, setData] = useState<ListingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${apiBaseUrl}/listing/${token}`)
      .then(async (r) => {
        if (r.status === 404) throw new Error('not_found');
        if (r.status === 410) throw new Error('revoked');
        if (!r.ok) throw new Error('load_failed');
        return (await r.json()) as ListingResponse;
      })
      .then(setData)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
      </div>
    );
  }
  if (error === 'revoked' || error === 'not_found') {
    return <UnavailableScreen reason={error} />;
  }
  if (error || !data) {
    return <UnavailableScreen reason="load_failed" />;
  }

  const { listing, photos, agent } = data;
  const priceText = listing.purchase_price
    ? formatMoney(Number(listing.purchase_price))
    : null;
  const titleAddress = [
    listing.address ?? `${listing.street ?? ''} ${listing.street_number ?? ''}`.trim(),
    listing.postal_code ? `${listing.postal_code} ${listing.city}` : listing.city,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-paper">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-8 md:py-4">
          <a href="https://4m-immo.com" className="flex items-center gap-2">
            <img src="/brand/logo-full.png" alt="4M Immobilien" className="h-7 w-auto" />
          </a>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-4 py-6 md:px-8 md:py-10">
        <PhotoGallery photos={photos} primaryUrl={listing.primary_image_url} />

        {/* Title + price block */}
        <section>
          <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--brand-gold-deep))]">
            {listing.district ?? listing.city}
            {listing.marketing_stage === 'active' || listing.marketing_stage === 'aufnahme' ? (
              <span className="ms-2 rounded-full bg-[hsl(var(--brand-gold))]/15 px-2 py-0.5 text-[10px] tracking-[0.06em] text-[hsl(var(--brand-gold-deep))]">
                {t('listing.exclusive', 'Off-Market exklusiv')}
              </span>
            ) : null}
          </p>
          <h1 className="mt-2 font-display text-3xl font-light leading-tight tracking-tight md:text-4xl">
            {titleAddress}
          </h1>
          {priceText && (
            <p className="mt-3 font-display text-3xl text-ink">{priceText}</p>
          )}
        </section>

        {/* KPI strip */}
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KPI label={t('listing.kpi.rooms', 'Zimmer')} value={listing.rooms_count ?? '—'} />
          <KPI
            label={t('listing.kpi.area', 'Wohnfläche')}
            value={listing.property_size ? `${listing.property_size} m²` : '—'}
          />
          <KPI label={t('listing.kpi.floor', 'Etage')} value={listing.floor ?? '—'} />
          <KPI
            label={t('listing.kpi.building_type', 'Bautyp')}
            value={germanBuildingType(listing.building_type)}
          />
        </section>

        {/* Marketing positioning strip — every public share reinforces the same hook */}
        <TrustBand />

        {/* Agent contact + inquiry form */}
        <section className="grid gap-6 md:grid-cols-[1fr_1.5fr]">
          {agent && <AgentCard agent={agent} />}
          <InquiryForm token={token} addressLine={titleAddress} />
        </section>
      </main>

      <footer className="mt-12 border-t border-line bg-paper-2">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-xs text-ink-3 md:px-8">
          <p>© 2026 4M Immobilien & Consulting GmbH & Co KG</p>
          <div className="flex gap-4">
            <a href="https://4m-immo.at/impressum" className="hover:text-ink">
              {t('home.footer.imprint', 'Impressum')}
            </a>
            <a href="https://4m-immo.at/datenschutz" className="hover:text-ink">
              {t('home.footer.privacy', 'Datenschutz')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Photo gallery ─────────────────────────────────────────────
function PhotoGallery({
  photos,
  primaryUrl,
}: {
  photos: ListingPhoto[];
  primaryUrl: string | null;
}) {
  // Real property photos take precedence; otherwise the primary thumbnail;
  // otherwise the Vienna panorama as a branded placeholder.
  const realSlides =
    photos.length > 0
      ? photos.map((p) => p.image_url)
      : primaryUrl
      ? [primaryUrl]
      : [];
  // Ein Foto, dessen Bytes nicht mehr da sind, darf keinen zerbrochenen
  // Rahmen auf einer Seite hinterlassen, die ein Makler einem Interessenten
  // geschickt hat. Fällt ein Bild aus, verschwindet es aus der Galerie; fällt
  // das letzte aus, greift die Wien-Ansicht.
  const [broken, setBroken] = useState<string[]>([]);
  const liveSlides = realSlides.filter((src) => !broken.includes(src));
  const usingFallback = liveSlides.length === 0;
  const slides = usingFallback ? [VIENNA_FALLBACK_PHOTO] : liveSlides;
  const [idx, setIdx] = useState(0);
  const safeIdx = idx < slides.length ? idx : 0;

  const prev = () => setIdx(((safeIdx - 1 + slides.length) % slides.length));
  const next = () => setIdx(((safeIdx + 1) % slides.length));

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl bg-paper-2">
        <img
          src={slides[safeIdx]}
          alt={usingFallback ? 'Wien · Panorama' : `Foto ${safeIdx + 1}`}
          onError={() => {
            const src = slides[safeIdx];
            if (!usingFallback && src) setBroken((b) => (b.includes(src) ? b : [...b, src]));
          }}
          className="h-[360px] w-full object-cover md:h-[480px]"
        />
      </div>
      {usingFallback && (
        <p className="absolute bottom-3 start-3 rounded-full bg-paper/90 px-3 py-1 text-[11px] tracking-wide text-ink-2">
          Foto folgt — Vorschau: Wien
        </p>
      )}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous"
            className="absolute start-3 top-1/2 -translate-y-1/2 rounded-full bg-paper/90 p-2 shadow-soft hover:bg-paper"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next"
            className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full bg-paper/90 p-2 shadow-soft hover:bg-paper"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="absolute bottom-3 end-3 rounded-full bg-paper/90 px-3 py-1 text-xs">
            {safeIdx + 1} / {slides.length}
          </p>
        </>
      )}
    </div>
  );
}

// Marketing trust band — surfaces 4M's positioning + AI suite directly
// below the property KPIs. Visible to everyone who clicks through a
// shared listing link, so every off-market exclusive doubles as a brand
// touchpoint. Subtle gold accents, no CTAs (the inquiry form below is the
// only call to action on this page).
function TrustBand() {
  const { t } = useTranslation();
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-[hsl(var(--brand-gold))]/25 bg-gradient-to-br from-[hsl(var(--brand-gold))]/5 via-paper to-paper px-5 py-5 md:px-7 md:py-6"
      aria-label="4M Immobilien"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#1a1305]"
            style={{
              background:
                'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
            }}
          >
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--brand-gold-deep))]">
              {t('listing.trust.eyebrow', 'Diskret. Mehrsprachig. Digital.')}
            </p>
            <p className="mt-1 font-display text-base leading-snug text-ink md:text-lg">
              {t(
                'listing.trust.headline',
                'Wiens erste vollständig digitalisierte Immobilienagentur.',
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 text-[11px] font-medium text-ink-2"
            title={t(
              'listing.trust.sophie_tooltip',
              'Sophie — unsere KI-Sekretärin nimmt Anfragen rund um die Uhr entgegen.',
            )}
          >
            <Phone className="h-3 w-3 text-[hsl(var(--brand-gold-deep))]" />
            {t('listing.trust.sophie', 'KI-Sekretärin Sophie')}
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 text-[11px] font-medium text-ink-2"
            title={t(
              'listing.trust.zoki_tooltip',
              'Zoki — KI-Assistent unterstützt unsere Berater in Echtzeit.',
            )}
          >
            <Bot className="h-3 w-3 text-[hsl(var(--brand-gold-deep))]" />
            {t('listing.trust.zoki', 'KI-Assistent Zoki')}
          </span>
        </div>
      </div>
    </section>
  );
}

function KPI({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-line bg-card px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.1em] text-ink-3">{label}</p>
      <p className="mt-1 font-display text-lg text-ink">{value}</p>
    </div>
  );
}

// ─── Agent card ───────────────────────────────────────────────
function AgentCard({ agent }: { agent: ListingAgent }) {
  const { t } = useTranslation();
  const initials = agent.full_name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="rounded-2xl border border-line bg-card p-5 shadow-soft">
      <p className="text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--brand-gold-deep))]">
        {t('listing.agent.label', 'Ihr Ansprechpartner')}
      </p>
      <div className="mt-4 flex items-center gap-4">
        <span
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-base font-semibold text-[#1a1305]"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
          }}
        >
          {initials}
        </span>
        <div>
          <p className="font-display text-lg leading-tight">{agent.full_name}</p>
          <p className="text-xs text-ink-3">{agent.title ?? 'Senior Advisor'}</p>
        </div>
      </div>
      <dl className="mt-4 space-y-2 text-sm">
        {agent.phone && (
          <div className="flex items-center justify-between">
            <dt className="text-ink-3">{t('listing.agent.phone', 'Telefon')}</dt>
            <dd>
              <a href={`tel:${agent.phone}`} className="font-medium text-ink hover:underline">
                {agent.phone}
              </a>
            </dd>
          </div>
        )}
        {agent.email && (
          <div className="flex items-center justify-between">
            <dt className="text-ink-3">{t('listing.agent.email', 'E-Mail')}</dt>
            <dd>
              <a href={`mailto:${agent.email}`} className="font-medium text-ink hover:underline">
                {agent.email}
              </a>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

// ─── Inquiry form ─────────────────────────────────────────────
function InquiryForm({ token, addressLine }: { token: string; addressLine: string }) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(
    t('listing.inquire.default_message', 'Ich interessiere mich für diese Immobilie und möchte gerne einen Besichtigungstermin vereinbaren.'),
  );
  // Honeypot — bots fill it, humans don't see it.
  const [hp, setHp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch(`${apiBaseUrl}/listing/${token}/inquire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          message: message.trim() || undefined,
          hp,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDone(true);
    } catch (cause) {
      setErr((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-line bg-card p-8 text-center shadow-soft">
        <CheckCircle2 className="h-12 w-12 text-[hsl(var(--positive))]" />
        <h3 className="mt-4 font-display text-xl">
          {t('listing.inquire.thanks_title', 'Vielen Dank!')}
        </h3>
        <p className="mt-2 max-w-sm text-sm text-ink-3">
          {t(
            'listing.inquire.thanks_body',
            'Ihre Anfrage ist bei uns angekommen. Sie hören in Kürze von Ihrem persönlichen Berater.',
          )}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-2xl border border-line bg-card p-5 shadow-soft">
      <div>
        <h3 className="font-display text-lg">
          {t('listing.inquire.title', 'Diskret anfragen')}
        </h3>
        <p className="text-xs text-ink-3">
          {t(
            'listing.inquire.subtitle',
            'Vertraulich. Unverbindlich. Ihre Daten bleiben bei 4M Immobilien.',
          )}
          <br />
          <span className="text-ink-3/80">{addressLine}</span>
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t('listing.inquire.first_name', 'Vorname')}
          value={firstName}
          onChange={setFirstName}
        />
        <Field
          label={t('listing.inquire.last_name', 'Nachname')}
          value={lastName}
          onChange={setLastName}
          required
        />
      </div>
      <Field
        label={t('listing.inquire.phone', 'Telefon')}
        value={phone}
        onChange={setPhone}
        type="tel"
        required
      />
      <Field
        label={t('listing.inquire.email', 'E-Mail')}
        value={email}
        onChange={setEmail}
        type="email"
      />
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-ink-3">
          {t('listing.inquire.message', 'Nachricht')}
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-line bg-paper-2 px-3 py-2 text-sm focus:border-[hsl(var(--brand-gold))] focus:outline-none"
        />
      </div>
      {/* Honeypot — visually hidden, screen readers ignore. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        style={{ position: 'absolute', left: '-10000px', height: 0, width: 0, overflow: 'hidden' }}
        aria-hidden="true"
      />
      {err && (
        <p className="flex items-center gap-2 text-xs text-[hsl(var(--danger))]">
          <AlertCircle className="h-3.5 w-3.5" />
          {t('listing.inquire.error', 'Anfrage konnte nicht gesendet werden — bitte erneut versuchen.')}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting || !lastName.trim() || !phone.trim()}
        className="inline-flex w-full items-center justify-center rounded-md px-4 py-3 text-sm font-semibold text-[#1a1305] shadow-soft transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
        }}
      >
        {submitting
          ? t('listing.inquire.submitting', 'Wird gesendet …')
          : t('listing.inquire.submit', 'Anfrage senden')}
      </button>
      <p className="text-[10px] leading-relaxed text-ink-3">
        {t(
          'listing.inquire.dsgvo',
          'Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß § 5–9 DSGVO zu. Wir verwenden Ihre Daten ausschließlich zur Bearbeitung Ihrer Anfrage.',
        )}
      </p>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-ink-3">
        {label} {required && <span className="text-[hsl(var(--danger))]">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-md border border-line bg-paper-2 px-3 py-2 text-sm focus:border-[hsl(var(--brand-gold))] focus:outline-none"
      />
    </div>
  );
}

function UnavailableScreen({ reason }: { reason: string }) {
  const { t } = useTranslation();
  const messages: Record<string, { title: string; body: string }> = {
    revoked: {
      title: t('listing.unavailable.revoked_title', 'Dieser Link wurde deaktiviert'),
      body: t(
        'listing.unavailable.revoked_body',
        'Der Inserent hat diesen Listing-Link zurückgezogen. Wenden Sie sich gerne direkt an uns für aktuelle Angebote.',
      ),
    },
    not_found: {
      title: t('listing.unavailable.notfound_title', 'Listing nicht gefunden'),
      body: t(
        'listing.unavailable.notfound_body',
        'Dieser Link ist ungültig oder abgelaufen.',
      ),
    },
    load_failed: {
      title: t('listing.unavailable.error_title', 'Etwas ist schiefgelaufen'),
      body: t(
        'listing.unavailable.error_body',
        'Bitte laden Sie die Seite neu oder versuchen Sie es später erneut.',
      ),
    },
  };
  const msg = messages[reason] ?? messages.load_failed!;
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="max-w-md text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-ink-3" />
        <h1 className="mt-6 font-display text-2xl text-ink">{msg.title}</h1>
        <p className="mt-2 text-sm text-ink-3">{msg.body}</p>
        <a
          href="https://4m-immo.com"
          className="mt-6 inline-flex rounded-md bg-ink px-4 py-2 text-sm font-semibold text-paper hover:bg-ink/90"
        >
          {t('listing.unavailable.home', 'Zur 4M Immobilien Webseite')}
        </a>
      </div>
    </div>
  );
}
